import {
  Button,
  CheckmarkIcon,
  Col,
  Container,
  getTypographyDefaultColor,
  Space,
  Typography
} from '@ag1/ui';
import { toPlainText } from '@portabletext/react';
import { useIsStore } from '@portal/state/hooks/useIsStore';
import { clickPurchaseIntentEvent } from 'analytics/events/studio';
import clsx from 'clsx';
import { useSetAtom } from 'jotai';
import { usePageContext } from 'processes/Page/PageContext';
import { useEffect, useRef } from 'react';
import { HsaFsaAvailability } from 'shared/components/HSA-FSA/HsaFsaAvailability';
import { getThemeCSSClass } from 'shared/utils/getThemeCSSClass';
import { useStoreValue } from 'state/hooks/useStore';
import { useUnfortunateWayToAppendCurrency } from 'state/hooks/useUnfortunateWayToAppendCurrency';
import { OfferWelcomeKit as OfferWelcomeKitProps } from 'types/studio/studio';
import { offerSectionAtom } from '../../../state/atoms/offerSectionAtom';
import { useGoToOffersV2 } from '../../hooks/useGoToOffersV2';
import CustomPortableText from '../PortableText/PortableText';
import { StudioHotspotImageV2 } from '../StudioHotspotImage/StudioHotspotImage';
import styles from './OfferWelcomeKit.module.scss';
import { CommonSectionProps } from '../types';
import { TrustpilotWidget } from '../TrustpilotWidget/TrustpilotWidget';

type Props = OfferWelcomeKitProps &
  CommonSectionProps & {
    _key: string;
  };

const OfferWelcomeKit = (props: Props) => {
  const {
    _type: sectionType,
    _key: key,
    offerImage,
    headline,
    subheader,
    offerTopSection,
    offerItemsTitle,
    offerItems,
    offerBottomSection,
    oldThemeButton,
    theme,
    offerButton,
    fullWidthButton,
    hoverColor,
    guarantees,
    contentPosition = 'left',
    bgColor,
    visibleOnPageLoad,
    oldPriceFirstToggle,
    widget
  } = props;
  const { disablePrimaryCTAButtons } = usePageContext();
  const setOfferSectionAtom = useSetAtom(offerSectionAtom);

  const { conditionallyAppendUSDtoPrice } = useUnfortunateWayToAppendCurrency();

  const sectionRef = useRef<HTMLDivElement>(null);
  const store = useStoreValue();
  const { isUsStore } = useIsStore();

  const isBgDark = bgColor === 'dark';

  useEffect(() => {
    if (sectionRef.current) {
      setOfferSectionAtom(sectionRef.current);
    }
  }, [setOfferSectionAtom, sectionRef]);

  const { goToOffer } = useGoToOffersV2({
    withScroll: false,
    bundleUrl: offerButton?.bundleUrl,
    buttonLink: offerButton?.buttonLink
  });

  const handleButtonClick = () => {
    if (disablePrimaryCTAButtons) {
      return;
    }
    const buttonText = offerButton?.baseButton?.portableText
      ? toPlainText(offerButton.baseButton.portableText)
      : '';
    clickPurchaseIntentEvent({
      store,
      clickText: buttonText,
      location: sectionType,
      url: offerButton?.bundleUrl || offerButton?.buttonLink
    });
    goToOffer();
  };

  return (
    <div
      className={clsx(
        styles.root,
        contentPosition === 'left' && styles.reverse,
        bgColor === 'stone' && styles.bgStone,
        bgColor === 'grey' && styles.bgGrey,
        isBgDark && styles.bgDark,
        getThemeCSSClass(theme)
      )}>
      <div className={clsx(styles.header, styles.mobile, subheader && styles.noPadding)}>
        <Typography
          variant="headlineSans"
          size="2"
          color={isBgDark ? 'brand-white' : getTypographyDefaultColor(theme)}
          noPadding>
          {headline}
        </Typography>
      </div>
      {subheader && (
        <Typography
          variant="subtitle"
          size="1"
          noPadding
          color={isBgDark ? 'brand-white' : getTypographyDefaultColor(theme)}
          className={clsx(styles.subheader, styles.mobile)}>
          <CustomPortableText value={subheader} />
        </Typography>
      )}

      {offerImage && (
        <Col className={styles.col} span={{ xxs: 12, m: 6 }}>
          <StudioHotspotImageV2
            className={clsx(
              styles.imageCont,
              contentPosition === 'left' ? styles.imageLeft : styles.imageRight
            )}
            image={offerImage}
            sizes={{ xxs: 12, m: 6, xxl: '900px' }}
            eagerLoading={visibleOnPageLoad}
          />
        </Col>
      )}
      <Col
        id="offers"
        ref={sectionRef}
        className={clsx(
          styles.col,
          styles.colInfo,
          styles[key],
          contentPosition === 'left' ? styles.colInfoLeft : styles.colInfoRight
        )}
        span={{ xxs: 12, m: 6 }}>
        <Container
          className={clsx(contentPosition === 'left' ? styles.clearRight : styles.clearLeft)}>
          <div className={styles.content}>
            <div className={clsx(styles.header, styles.desktop)}>
              <Typography
                variant="headlineSans"
                size="2"
                component="h2"
                color={isBgDark ? 'brand-white' : getTypographyDefaultColor(theme)}
                noPadding>
                {headline}
              </Typography>
            </div>
            {subheader && (
              <Typography
                variant="subtitle"
                size="1"
                noPadding
                color={isBgDark ? 'brand-white' : getTypographyDefaultColor(theme)}
                className={clsx(styles.subheader, styles.desktop)}>
                <CustomPortableText value={subheader} />
              </Typography>
            )}
            <div className={styles.table}>
              {offerTopSection?.itemTopHeadline && (
                <div className={clsx(styles.tableItem, styles.top, getThemeCSSClass(theme))}>
                  <Typography
                    color={isBgDark ? 'brand-white' : getTypographyDefaultColor(theme)}
                    variant="body"
                    size="2"
                    weight="700"
                    noPadding
                    className={styles.tableHeadline}>
                    {offerTopSection?.itemTopHeadline}
                  </Typography>
                  <Typography
                    color={isBgDark ? 'brand-white' : getTypographyDefaultColor(theme)}
                    variant="portalCaption"
                    size="1"
                    noPadding
                    className={clsx(
                      styles.tableDescription,
                      getThemeCSSClass(theme),
                      isBgDark && styles.textWhite
                    )}>
                    {offerTopSection?.itemTopDescription}
                  </Typography>
                  <div
                    className={clsx(
                      styles.tablePrice,
                      oldPriceFirstToggle && styles.flexRowReverse
                    )}>
                    <Typography
                      color={isBgDark ? 'brand-white' : getTypographyDefaultColor(theme)}
                      variant="body"
                      size="2"
                      weight="700"
                      noPadding
                      aria-label="current price"
                      className={styles.tablePriceAlt}>
                      {conditionallyAppendUSDtoPrice(offerTopSection?.itemTopAlternativePrice)}
                    </Typography>
                    {offerTopSection?.itemTopBasePrice && (
                      <Typography
                        color={isBgDark ? 'brand-white' : getTypographyDefaultColor(theme)}
                        variant="body"
                        size="2"
                        aria-label="original price struck through"
                        noPadding
                        className={clsx(
                          styles.tablePriceBasic,
                          getThemeCSSClass(theme),
                          isBgDark && styles.textWhite
                        )}>
                        <s>{offerTopSection?.itemTopBasePrice}</s>
                        <sup>†</sup>
                      </Typography>
                    )}
                  </div>
                </div>
              )}
              {offerItemsTitle && (
                <Typography
                  color={isBgDark ? 'brand-white' : getTypographyDefaultColor(theme)}
                  variant="captionMono"
                  size="1"
                  className={clsx(
                    styles.tableItemsHeadline,
                    getThemeCSSClass(theme),
                    isBgDark && styles.textWhite
                  )}>
                  {offerItemsTitle}
                </Typography>
              )}
              {offerItems &&
                offerItems.map(
                  (item) =>
                    item.itemHeadline && (
                      <div key={item._key} className={styles.tableItem}>
                        <Typography
                          color={isBgDark ? 'brand-white' : getTypographyDefaultColor(theme)}
                          variant="body"
                          size="2"
                          weight="700"
                          noPadding
                          className={styles.tableHeadline}>
                          {item.itemHeadline}
                        </Typography>
                        <Typography
                          color={isBgDark ? 'brand-white' : getTypographyDefaultColor(theme)}
                          variant="portalCaption"
                          size="1"
                          noPadding
                          className={clsx(
                            styles.tableDescription,
                            getThemeCSSClass(theme),
                            isBgDark && styles.textWhite
                          )}>
                          {item.itemDescription}
                        </Typography>
                        <div
                          className={clsx(
                            styles.tablePrice,
                            oldPriceFirstToggle && styles.flexRowReverse
                          )}>
                          <Typography
                            color={isBgDark ? 'brand-white' : getTypographyDefaultColor(theme)}
                            variant="body"
                            size="2"
                            weight="700"
                            noPadding
                            aria-label="current price"
                            className={styles.tablePriceAlt}>
                            {conditionallyAppendUSDtoPrice(item.itemAlternativePrice)}
                          </Typography>
                          <Typography
                            color={isBgDark ? 'brand-white' : getTypographyDefaultColor(theme)}
                            variant="body"
                            size="2"
                            noPadding
                            aria-label="original price struck through"
                            className={clsx(
                              styles.tablePriceBasic,
                              getThemeCSSClass(theme),
                              isBgDark && styles.textWhite
                            )}>
                            <s>{item.itemBasePrice}</s>
                          </Typography>
                        </div>
                      </div>
                    )
                )}
              {offerBottomSection?.itemBottomHeadline && (
                <div className={clsx(styles.tableItem, styles.bottom, getThemeCSSClass(theme))}>
                  <Typography
                    color={isBgDark ? 'brand-white' : getTypographyDefaultColor(theme)}
                    variant="body"
                    size="2"
                    weight="700"
                    noPadding
                    className={styles.tableHeadline}>
                    {offerBottomSection?.itemBottomHeadline}
                  </Typography>
                  <div
                    className={clsx(
                      styles.tablePrice,
                      oldPriceFirstToggle && styles.flexRowReverse
                    )}>
                    <Typography
                      color={isBgDark ? 'brand-white' : getTypographyDefaultColor(theme)}
                      variant="body"
                      size="2"
                      weight="700"
                      noPadding
                      className={styles.tablePriceAlt}>
                      {conditionallyAppendUSDtoPrice(
                        offerBottomSection?.itemBottomAlternativePrice
                      )}
                    </Typography>
                    {offerBottomSection?.itemBottomBasePrice && (
                      <Typography
                        color={isBgDark ? 'brand-white' : getTypographyDefaultColor(theme)}
                        variant="body"
                        size="2"
                        noPadding
                        className={clsx(
                          styles.tablePriceBasic,
                          getThemeCSSClass(theme),
                          isBgDark && styles.textWhite
                        )}>
                        <s>{offerBottomSection?.itemBottomBasePrice}</s>
                      </Typography>
                    )}
                  </div>
                </div>
              )}
            </div>
            {offerTopSection?.itemTopBasePrice && isUsStore && (
              <Typography
                color={isBgDark ? 'brand-white' : getTypographyDefaultColor(theme)}
                variant="portalCaption"
                size="1"
                noPadding
                className={clsx(
                  styles.disclaimer,
                  getThemeCSSClass(theme),
                  isBgDark && styles.textWhite
                )}>
                <sup>†</sup> Value of one-time purchase
              </Typography>
            )}
            {offerButton && offerButton.baseButton && (
              <Space top={{ xxs: 16, s: 32, l: 40 }} className={styles.buttonWrapper}>
                <Button
                  size={{ xxs: 'medium', l: 'large' }}
                  theme={oldThemeButton ? 'old' : theme}
                  variant={offerButton.baseButton.variant}
                  fluid
                  rounded
                  icon={offerButton.baseButton.showIcon && offerButton.baseButton.iconOptions?.icon}
                  iconPosition={offerButton.baseButton.iconOptions?.position}
                  className={clsx(
                    styles.button,
                    hoverColor === 'dark' && styles.dark,
                    fullWidthButton && styles.button_fullWidth
                  )}
                  data-gtm-skip-global
                  onClick={handleButtonClick}
                  disabled={disablePrimaryCTAButtons}>
                  {offerButton.baseButton.portableText && (
                    <CustomPortableText value={offerButton.baseButton.portableText} />
                  )}
                </Button>
                <HsaFsaAvailability sourcePage="primaryLander" />
              </Space>
            )}
            {guarantees?.length && (
              <div className={styles.guarantees}>
                {guarantees?.map((item) => (
                  <div key={item} className={styles.guarantee}>
                    <Space
                      right={6}
                      bottom={2}
                      className={clsx(styles.guaranteeIcon, isBgDark && styles.iconWhite)}>
                      <CheckmarkIcon size={12} />
                    </Space>
                    <Typography
                      color={isBgDark ? 'brand-white' : getTypographyDefaultColor(theme)}
                      variant="portalCaption"
                      size="1"
                      noPadding>
                      {item}
                    </Typography>
                  </div>
                ))}
              </div>
            )}
            {widget && (
              <TrustpilotWidget
                _key="tpw"
                {...widget}
                width="100%"
                visibleOnPageLoad={false}
                className={styles.trustpilott}
              />
            )}
          </div>
        </Container>
      </Col>
    </div>
  );
};

export default OfferWelcomeKit;
